import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import useAttributes, {
    AttributeContextType,
    AttributeDefinitionDto,
    AttributeDto,
    AttributeEntityDto,
    AttributeViewType,
    EntityAttributeType,
    evaluateDependencyValue,
    isAttributeValueNonNull,
} from '../../../hooks/useAttributes'
import { Field, InfoLabel, makeStyles, mergeClasses, MessageBar, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import AttributeDefinitionRow from './AttributeDefinitionRow'

export type KeyValue = {
    key: string
    value: string
}

export const useAttributesStyles = makeStyles({
    fieldLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: tokens.spacingVerticalXS,
    },
    fieldLabelDisabled: {
        textDecorationLine: 'line-through',
    },
    fieldNonFirst: {
        marginTop: tokens.spacingVerticalXS,
    },
})

export type AttributesListProps = {
    attributeDefinitions?: AttributeDefinitionDto[]
    orientation: 'horizontal' | 'vertical'
    entity: AttributeEntityDto
    entityId?: string
    entityType: EntityAttributeType
    context: AttributeContextType[]
    viewType: AttributeViewType
    category?: string
    setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void
}

export const parseAttributeToParams = (attributes?: AttributeDto[]): string[] => {
    return attributes
        ?.filter((value, index, array) => array.indexOf(value) === index)
        .filter(att => isAttributeValueNonNull(att.value))
        .map(attribute => JSON.stringify(attribute))
}

export default function AttributesList(props: AttributesListProps): ReactElement {
    const { attributeDefinitions } = useAttributes(props.viewType, props.context, props.category)
    const styles = useAttributesStyles()
    const { t: translate } = useTranslation()

    const visibleAttributes = useMemo(
        () => props.attributeDefinitions ?? attributeDefinitions,
        [attributeDefinitions, props.attributeDefinitions, props.entity.attributes],
    )

    if (visibleAttributes.length === 0) {
        return (
            <MessageBar intent="info" layout={'multiline'}>
                {translate('attribute.empty')}
            </MessageBar>
        )
    }

    return (
        <>
            {visibleAttributes
                //filter definition based on enabled or existing value.
                ?.filter(definition => definition.enabled || props.entity.attributes?.some(attribute => attribute.definitionId === definition.id))
                //filter definition based on cross attribute dependencies.
                ?.filter(definition => {
                    if (!definition.dependencies) {
                        return true
                    }

                    return definition.dependencies?.some(dependency => {
                        const foundAttribute = props.entity.attributes?.find(att => att.definitionId === dependency.id)
                        if (!foundAttribute) {
                            return false
                        }

                        if (dependency.value) {
                            return (
                                dependency.value.includes(foundAttribute?.value) ||
                                (Array.isArray(foundAttribute?.value) && dependency.value.some(r => foundAttribute?.value.includes(r)))
                            )
                        }

                        if (dependency.evaluation) {
                            return evaluateDependencyValue(foundAttribute, dependency.evaluation)
                        }

                        return false
                    })
                })
                .map((definition, index) => (
                    <Field
                        key={`attributes-${index}`}
                        orientation={props.orientation}
                        className={index > 0 && styles.fieldNonFirst}
                        required={definition.properties?.required}
                        label={{
                            children: (
                                <InfoLabel
                                    className={mergeClasses(styles.fieldLabel, !definition.enabled && styles.fieldLabelDisabled)}
                                    info={[!definition.enabled ? translate('attribute.field.inactive') : undefined, definition.description]
                                        .filter(value => value !== undefined)
                                        .reduce((a, b) => a + ' ' + b)}
                                >
                                    {definition.name}
                                </InfoLabel>
                            ),
                        }}
                    >
                        <AttributeDefinitionRow {...props} definition={definition} />
                    </Field>
                ))}
        </>
    )
}
